import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Upload, Camera, X, FileText } from "lucide-react";
import useMultipleFileUpload from "@/hooks/useMultipleFileUpload";
import { DocumentManager } from "./DocumentManager";
import { useCandidateDraft } from "@/hooks/useCandidateDraft";

interface SecondInterviewFormProps {
  candidateId: string;
  candidateName?: string;
  interviewId?: string;
  onComplete: (shouldScheduleSecond?: boolean) => void;
  onCancel?: () => void;
  viewMode?: boolean; // Add view mode prop
}

export function SecondInterviewForm({ candidateId, candidateName, interviewId, onComplete, onCancel, viewMode = false }: SecondInterviewFormProps) {
  // Use secure database-backed draft instead of localStorage
  const { draft, saveDraft, deleteDraft, isSaving } = useCandidateDraft('second_interview', candidateId);
  
  const [formData, setFormData] = useState(() => {
    // Load from secure database draft
    if (draft) {
      return draft;
    }
    
    // Default values
    return {
      // Personal Information
      firstName: "",
      lastName: "",
      fatherFirstName: "",
      fatherLastName: "",
      motherFirstName: "",
      motherLastName: "",
      
      // Address
      streetAddress: "",
      streetAddress2: "",
      city: "",
      state: "",
      postalCode: "",
      
      // Contact
      homeAreaCode: "",
      homePhone: "",
      mobileAreaCode: "",
      mobilePhone: "",
      email: "",
      
      // Personal Details
      maritalStatus: "",
      numberOfChildren: "",
      idNumber: "",
      
      // Licenses and Documents
      securityLicense: "",
      ankaNumber: "",
      amaNumber: "",
      
      // Additional fields needed for guard creation
      afm: "",
      hiringDate: "",
      dateOfBirth: "",
      licenseExpiryDate: "",
      
      // Uploaded document URLs
      documentUrls: [] as string[]
    };
  });

  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  const { 
    files, 
    handleFileSelect, 
    handleFileDrop, 
    removeFile, 
    uploadFiles,
    isUploading,
    captureImage 
  } = useMultipleFileUpload();

  // Save to database whenever formData changes (debounced via hook)
  useEffect(() => {
    if (!viewMode) {
      const hasData = Object.values(formData).some(value => 
        Array.isArray(value) ? value.length > 0 : value !== ""
      );
      if (hasData) {
        saveDraft(formData);
      }
    }
  }, [formData, viewMode]);

  // Prevent accidental navigation/refresh when there's unsaved data
  useEffect(() => {
    const hasUnsavedData = Object.values(formData).some(value => 
      Array.isArray(value) ? value.length > 0 : value !== ""
    );

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedData) {
        e.preventDefault();
        e.returnValue = "Έχετε μη αποθηκευμένα δεδομένα. Είστε σίγουροι ότι θέλετε να φύγετε;";
        return e.returnValue;
      }
    };

    if (hasUnsavedData) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDocumentDeleted = (deletedUrl: string) => {
    setFormData(prev => ({
      ...prev,
      documentUrls: prev.documentUrls.filter(url => url !== deletedUrl)
    }));
  };

  // Load existing interview data if editing
  useEffect(() => {
    if (interviewId) {
      const loadInterviewData = async () => {
        try {
          const { data, error } = await supabase
            .from('interviews')
            .select('interview_data')
            .eq('id', interviewId)
            .single();

          if (error) throw error;

          if (data?.interview_data) {
            const loadedData = data.interview_data as typeof formData;
            setFormData({
              ...loadedData,
              documentUrls: Array.isArray(loadedData.documentUrls) ? loadedData.documentUrls : []
            });
          }
        } catch (error) {
          console.error('Error loading interview data:', error);
          toast({
            title: "Σφάλμα",
            description: "Δεν ήταν δυνατή η φόρτωση των δεδομένων της συνέντευξης",
            variant: "destructive",
          });
        }
      };

      loadInterviewData();
    }
  }, [interviewId, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // First upload any new documents
      const newDocumentUrls = await uploadFiles('candidate-documents');
      
      // Update formData with both existing and new document URLs
      const updatedFormData = {
        ...formData,
        documentUrls: [...formData.documentUrls, ...newDocumentUrls]
      };
      
      // Update existing interview record
      if (interviewId) {
        const { error: interviewError } = await supabase
          .from('interviews')
          .update({
            status: 'completed',
            interview_data: updatedFormData,
            completed_at: new Date().toISOString()
          })
          .eq('id', interviewId);

        if (interviewError) throw interviewError;
      } else {
        // Create new interview record
        const { error: interviewError } = await supabase
          .from('interviews')
          .insert([{
            candidate_id: candidateId,
            interview_type: 'second',
            status: 'completed',
            scheduled_date: new Date().toISOString(),
            interview_data: updatedFormData,
            completed_at: new Date().toISOString()
          }]);

        if (interviewError) throw interviewError;
      }

      // Update candidate status to make tests available
      const { error: updateError } = await supabase
        .from('candidates')
        .update({ status: 'tests_available' })
        .eq('id', candidateId);
        
      if (updateError) throw updateError;
      
      toast({
        title: "Επιτυχία",
        description: "Η δεύτερη συνέντευξη καταγράφηκε επιτυχώς",
      });

      // Clear saved data after successful submission
      await deleteDraft();

      onComplete();
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message || "Αποτυχία αποθήκευσης συνέντευξης",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>
          {viewMode ? 'Προβολή' : (interviewId ? 'Επεξεργασία' : 'Καταγραφή Στοιχείων')} - Δεύτερη Συνέντευξη
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {viewMode ? 'Προβολή των στοιχείων του υποψηφίου' : (interviewId ? 'Επεξεργασία των στοιχείων του υποψηφίου' : 'Παρακαλώ συμπληρώστε τα προσωπικά στοιχεία του υποψηφίου')}: {candidateName}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Name Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Ονοματεπώνυμο</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">Όνομα</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="First Name"
                  required
                  readOnly={viewMode}
                />
              </div>
              <div>
                <Label htmlFor="lastName">Επώνυμο</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Last Name"
                  required
                  readOnly={viewMode}
                />
              </div>
            </div>
          </div>

          {/* Father's Name */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Πατρώνυμο</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fatherFirstName">Όνομα Πατέρα</Label>
                <Input
                  id="fatherFirstName"
                  value={formData.fatherFirstName}
                  onChange={(e) => handleInputChange('fatherFirstName', e.target.value)}
                  placeholder="First Name"
                  readOnly={viewMode}
                />
              </div>
              <div>
                <Label htmlFor="fatherLastName">Επώνυμο Πατέρα</Label>
                <Input
                  id="fatherLastName"
                  value={formData.fatherLastName}
                  onChange={(e) => handleInputChange('fatherLastName', e.target.value)}
                  placeholder="Last Name"
                  readOnly={viewMode}
                />
              </div>
            </div>
          </div>

          {/* Mother's Name */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Μητρώνυμο</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="motherFirstName">Όνομα Μητέρας</Label>
                <Input
                  id="motherFirstName"
                  value={formData.motherFirstName}
                  onChange={(e) => handleInputChange('motherFirstName', e.target.value)}
                  placeholder="First Name"
                  readOnly={viewMode}
                />
              </div>
              <div>
                <Label htmlFor="motherLastName">Επώνυμο Μητέρας</Label>
                <Input
                  id="motherLastName"
                  value={formData.motherLastName}
                  onChange={(e) => handleInputChange('motherLastName', e.target.value)}
                  placeholder="Last Name"
                  readOnly={viewMode}
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Διεύθυνση Κατοικίας</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="streetAddress">Διεύθυνση</Label>
                <Input
                  id="streetAddress"
                  value={formData.streetAddress}
                  onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                  placeholder="Street Address"
                  required
                  readOnly={viewMode}
                />
              </div>
              <div>
                <Label htmlFor="streetAddress2">Διεύθυνση 2</Label>
                <Input
                  id="streetAddress2"
                  value={formData.streetAddress2}
                  onChange={(e) => handleInputChange('streetAddress2', e.target.value)}
                  placeholder="Street Address Line 2"
                  readOnly={viewMode}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">Πόλη</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="City"
                    required
                    readOnly={viewMode}
                  />
                </div>
                <div>
                  <Label htmlFor="state">Νομός / Περιφέρεια</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="State / Province"
                    readOnly={viewMode}
                  />
                </div>
                <div>
                  <Label htmlFor="postalCode">Ταχυδρομικός Κώδικας</Label>
                  <Input
                    id="postalCode"
                    value={formData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    placeholder="Postal / Zip Code"
                    readOnly={viewMode}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Home Phone */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Τηλέφωνο Οικίας</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="homeAreaCode">Κωδικός Περιοχής</Label>
                <Input
                  id="homeAreaCode"
                  value={formData.homeAreaCode}
                  onChange={(e) => handleInputChange('homeAreaCode', e.target.value)}
                  placeholder="Area Code"
                  readOnly={viewMode}
                />
              </div>
              <div>
                <Label htmlFor="homePhone">Αριθμός Τηλεφώνου</Label>
                <Input
                  id="homePhone"
                  value={formData.homePhone}
                  onChange={(e) => handleInputChange('homePhone', e.target.value)}
                  placeholder="Phone Number"
                  readOnly={viewMode}
                />
              </div>
            </div>
          </div>

          {/* Mobile Phone */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Κινητό Τηλέφωνο</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="mobileAreaCode">Κωδικός Περιοχής</Label>
                <Input
                  id="mobileAreaCode"
                  value={formData.mobileAreaCode}
                  onChange={(e) => handleInputChange('mobileAreaCode', e.target.value)}
                  placeholder="Area Code"
                  readOnly={viewMode}
                />
              </div>
              <div>
                <Label htmlFor="mobilePhone">Αριθμός Τηλεφώνου</Label>
                <Input
                  id="mobilePhone"
                  value={formData.mobilePhone}
                  onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
                  placeholder="Phone Number"
                  required
                  readOnly={viewMode}
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="username@example.com"
                  required
                  readOnly={viewMode}
                />
              </div>
            </div>
          </div>

          {/* Marital Status */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Οικογενειακή Κατάσταση</h3>
            <RadioGroup
              value={formData.maritalStatus}
              onValueChange={viewMode ? undefined : (value) => handleInputChange('maritalStatus', value)}
              disabled={viewMode}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="married" id="married" />
                <Label htmlFor="married">ΕΓΓΑΜΟΣ</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="divorced" id="divorced" />
                <Label htmlFor="divorced">ΔΙΑΖΕΥΓΜΕΝΟΣ</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="single" id="single" />
                <Label htmlFor="single">ΑΝΥΠΑΝΔΡΟΣ</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Number of Children */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Αριθμός Τέκνων</h3>
            <Input
              value={formData.numberOfChildren}
              onChange={(e) => handleInputChange('numberOfChildren', e.target.value)}
              placeholder="ex. 2"
              type="number"
              min="0"
              readOnly={viewMode}
            />
          </div>

          {/* ID Number */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Αριθμός ΑΔΤ</h3>
            <Input
              value={formData.idNumber}
              onChange={(e) => handleInputChange('idNumber', e.target.value)}
              placeholder="ex. 23"
              required
              readOnly={viewMode}
            />
          </div>

          {/* Security License */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Άδεια Εργασίας Προσωπικού Ασφαλείας</h3>
            <Input
              value={formData.securityLicense}
              onChange={(e) => handleInputChange('securityLicense', e.target.value)}
              placeholder="Αριθμός άδειας"
              readOnly={viewMode}
            />
          </div>

          {/* ANKA Number */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Αριθμός ΑΜΚΑ</h3>
            <Input
              value={formData.ankaNumber}
              onChange={(e) => handleInputChange('ankaNumber', e.target.value)}
              placeholder="ex. 23"
              readOnly={viewMode}
            />
          </div>

          {/* AMA Number */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Αριθμός ΑΜΑ</h3>
            <Input
              value={formData.amaNumber}
              onChange={(e) => handleInputChange('amaNumber', e.target.value)}
              placeholder="ex. 23"
              readOnly={viewMode}
            />
          </div>

          {/* VAT */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">ΦΠΑ <span className="text-red-500">*</span></h3>
            <Input
              value={formData.afm}
              onChange={(e) => handleInputChange('afm', e.target.value)}
              placeholder="Αριθμός Φορολογικού Μητρώου"
              required
              readOnly={viewMode}
            />
          </div>

          {/* Date of Birth */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Ημερομηνία Γέννησης</h3>
            <Input
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
              readOnly={viewMode}
            />
          </div>

          {/* Hiring Date */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Ημερομηνία Πρόσληψης</h3>
            <Input
              type="date"
              value={formData.hiringDate}
              onChange={(e) => handleInputChange('hiringDate', e.target.value)}
              readOnly={viewMode}
            />
          </div>

          {/* License Expiry Date */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Ημερομηνία Λήξης Άδειας</h3>
            <Input
              type="date"
              value={formData.licenseExpiryDate}
              onChange={(e) => handleInputChange('licenseExpiryDate', e.target.value)}
              readOnly={viewMode}
            />
          </div>

          {/* Document Upload Section */}
          {!viewMode && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Έγγραφα & Φωτογραφίες</h3>
              <div 
                className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:bg-muted/50 transition"
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.multiple = true;
                  input.accept = 'image/*, application/pdf';
                  input.onchange = (e) => handleFileSelect((e.target as HTMLInputElement).files);
                  input.click();
                }}
              >
                <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-lg font-medium">Σύρετε αρχεία εδώ ή κάντε κλικ για επιλογή</p>
                <p className="text-sm text-muted-foreground mt-1">Υποστηρίζονται εικόνες και PDF</p>
              </div>
              
              <div className="flex justify-start my-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={captureImage}
                  className="flex items-center gap-2"
                >
                  <Camera className="h-4 w-4" />
                  Λήψη Φωτογραφίας
                </Button>
              </div>
            
            {/* File previews */}
            {files.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {files.map((file) => (
                  <div key={file.id} className="relative border rounded-md overflow-hidden group">
                    {file.file.type.startsWith('image/') ? (
                      <img 
                        src={file.previewUrl} 
                        alt={file.file.name} 
                        className="w-full h-32 object-cover" 
                      />
                    ) : (
                      <div className="w-full h-32 bg-muted flex items-center justify-center">
                        <FileText className="h-10 w-10 text-muted-foreground" />
                      </div>
                    )}
                    <div className="p-2 bg-card">
                      <p className="text-sm truncate font-medium">{file.file.name}</p>
                      <p className="text-xs text-muted-foreground">{(file.file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(file.id);
                      }}
                      className="absolute top-1 right-1 bg-background/80 rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    {file.uploading && (
                      <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
                        <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Document Manager */}
            <div className="mt-6">
              <DocumentManager
                documents={formData.documentUrls}
                onDocumentDeleted={viewMode ? undefined : handleDocumentDeleted}
                viewMode={viewMode}
              />
            </div>
          </div>
          )}

          {/* Display Documents in View Mode */}
          {viewMode && formData.documentUrls.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Έγγραφα & Φωτογραφίες</h3>
              <DocumentManager
                documents={formData.documentUrls}
                viewMode={true}
              />
            </div>
          )}

          <div className="flex justify-end space-x-2">
            {onCancel && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  if (viewMode) {
                    onCancel();
                    return;
                  }
                  
                  const hasUnsavedData = Object.values(formData).some(value => 
                    Array.isArray(value) ? value.length > 0 : value !== ""
                  );
                  
                  if (hasUnsavedData) {
                    if (window.confirm("Έχετε μη αποθηκευμένα δεδομένα. Είστε σίγουροι ότι θέλετε να ακυρώσετε;")) {
                      deleteDraft();
                      onCancel();
                    }
                  } else {
                    onCancel();
                  }
                }}
              >
                {viewMode ? "Κλείσιμο" : "Ακύρωση"}
              </Button>
            )}
            {!viewMode && (
              <Button type="submit" disabled={loading || isUploading}>
                {loading ? "Αποθήκευση..." : "Καταγραφή Στοιχείων"}
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}